import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, Tooltip, styled, withStyles } from '@material-ui/core';
import InputGroup from '../../Common/Form/InputGroup';
import TransferTable from '../../Common/Form/TransferTable';
import { formatDate } from '../../../helpers/date';
import { converDecimalToPercentage } from '../../../helpers/number';

const propTypes = {
  name: PropTypes.string.isRequired,
  kobeZones: PropTypes.arrayOf(PropTypes.string).isRequired,
  formMethods: PropTypes.shape({
    control: PropTypes.object,
    errors: PropTypes.object,
    trigger: PropTypes.func,
    watch: PropTypes.func,
  }).isRequired,
  proxyEvents: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  excludedProxyEventIds: PropTypes.object.isRequired,
  handleProxyEventsChange: PropTypes.func.isRequired,
};
const defaultProps = {};

const TRANSFER_TABLE_COLUMNS = [
  { id: 'rank', header: 'Rank', align: 'center', width: 40 },
  { id: 'lobsang_event_id', header: 'Event ID', width: 75 },
  { id: 'performer', header: 'Event Name', width: 90 },
  { id: 'proxy_zone', header: 'Proxy Zone', width: 90 },
  { id: 'venue', header: 'Venue', width: 80 },
  { id: 'event_date', header: 'Date', width: 70 },
  { id: 'similarity', header: 'Similarity', align: 'center', width: 50 },
];

const StyledTooltip = withStyles({
  tooltip: {
    fontSize: '14px',
  },
})(Tooltip);

const TruncatedLink = styled(Link)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
}));

const TruncatedText = styled('div')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const compareProxyEvents = (a, b) => a.rank - b.rank;

const formatProxyEvent = proxyEvent => ({
  ...proxyEvent,
  id: proxyEvent.lobsang_event_id, // Required for TransferTable.
  performer: (
    <StyledTooltip title={proxyEvent.performer} placement="top" arrow>
      <TruncatedLink
        href={`${process.env.KONAMI_URL}/event/dashboard/${proxyEvent.lobsang_event_id}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        {proxyEvent.performer}
      </TruncatedLink>
    </StyledTooltip>
  ),
  venue: (
    <StyledTooltip title={proxyEvent.venue} placement="top" arrow>
      <TruncatedText>{proxyEvent.venue}</TruncatedText>
    </StyledTooltip>
  ),
  event_date: formatDate(proxyEvent.event_date),
  similarity: proxyEvent.similarity ? converDecimalToPercentage(proxyEvent.similarity) : null,
});

function KobeZoneProxies({
  name,
  formMethods,
  proxyEvents,
  isLoading,
  excludedProxyEventIds,
  handleProxyEventsChange,
}) {
  const { watch } = formMethods;
  const currentKobeZones = watch(name);

  const getIncludedProxyEvents = (zoneName, proxies) => {
    const includedEvents = proxies
      .filter(event => !excludedProxyEventIds[zoneName]?.includes(event.lobsang_event_id))
      .sort(compareProxyEvents)
      .map(formatProxyEvent);
    return includedEvents;
  };

  const getExcludedProxyEvents = (zoneName, proxies) => {
    const excludedEvents = proxies
      .filter(event => excludedProxyEventIds[zoneName]?.includes(event.lobsang_event_id))
      .sort(compareProxyEvents)
      .map(formatProxyEvent);

    return excludedEvents;
  };

  useEffect(() => {
    const zones = Object.keys(proxyEvents).filter(key => !currentKobeZones?.some(zone => zone.zone === key));
    if (zones.length > 0) {
      zones.forEach(zone => {
        const includedEvents = proxyEvents[zone];
        const excludedEvents = [];
        handleProxyEventsChange(zone, includedEvents, excludedEvents);
      });
    }
  }, [currentKobeZones]);

  return Object.keys(proxyEvents)
    .filter(key => currentKobeZones?.some(zone => zone.zone === key))
    .map(key => (
      <InputGroup key={key} label="Remove/Add Proxy Zones" title={key}>
        <TransferTable
          disabled={isLoading}
          columns={TRANSFER_TABLE_COLUMNS}
          actionColumnWidth={25}
          includedItems={getIncludedProxyEvents(key, proxyEvents[key])}
          excludedItems={getExcludedProxyEvents(key, proxyEvents[key])}
          onChange={(included, excluded) => handleProxyEventsChange(key, included, excluded)}
        />
      </InputGroup>
    ));
}

KobeZoneProxies.propTypes = propTypes;
KobeZoneProxies.defaultProps = defaultProps;

export default KobeZoneProxies;
